/* eslint-disable import/no-unresolved */
import Sortable from 'sortablejs';
import { PristineValidation } from '~/globals/form-validation/form-validation';
import { ProjectCostBreakdown } from '~/marketplace/project-cost-breakdown';
import { MilestoneManager } from '~/marketplace/milestone-manager';
import { ServiceSelectMediaDialog } from '~/services/service-select-media-dialog';
import { shakeElement } from '~/shared/utils/animation';
import { generateResizedMediaUrl } from '~/shared/utils/media';
import { getCSRFToken } from '~/shared/utils/request';
import { DrbConfirm } from '~/web-components/drb-confirm/drb-confirm-helpers';
import { DrbDialog } from '~/web-components/drb-dialog/drb-dialog-helpers';
import { initProCalloutBanners } from '~/shared/modules/pro-callout-banner';
import questionIcon from '~/assets/icons/v2-confirm-question.svg?raw';

export const ServiceFormDialog = {
  init() {
    this.bindListeners();
    this.performQueryParamActions();
    ProjectCostBreakdown.init();
  },

  performQueryParamActions() {
    const url = new URL(window.location.href);
    const editServiceId = url.searchParams.get('edit_service');

    if (editServiceId) {
      DrbDialog.openRemote({
        url: `/services/${editServiceId}/edit`,
        dialogId: 'service-form-dialog',
        shouldReload: true,
        preventLightDismiss: true,
      });
    }

    // remove query param after performing action
    url.searchParams.delete('edit_service');
    window.history.replaceState({}, document.title, url.toString());
  },

  bindListeners() {
    document.addEventListener('drb-dialog-content-updated', (e) => {
      const dialog = e.target;
      const form = dialog.querySelector('[data-service-form]');
      if (!form) return;

      this.setupSortable(dialog);
      this.bindFormSubmit(form);
      this.bindFormValidation(form);
      this.bindTimeSelectPluralize(form);
      this.bindSelectMediaDialog(form, dialog);
      this.bindMediaListeners(form);
      this.bindProjectCostBreakdown(form);
      this.bindDeleteButton(form);

      form.milestoneManager = new MilestoneManager(
        form,
        () => this.bindFormValidation(form),
        form.querySelector('#service-form-price'),
      );

      if (!Dribbble?.JsConfig?.user?.hasPro) {
        initProCalloutBanners();
      }
    });
  },

  setupSortable(dialog) {
    const mediaItems = dialog.querySelector('[data-service-form-media-items]');

    new Sortable(mediaItems, {
      animation: 100,
      removeCloneOnHide: false,
      draggable: '[data-service-form-media-item]',
      ghostClass: 'service-form-media-item--ghost',
      dragClass: 'service-form-media-item--dragging',
    });
  },

  bindFormSubmit(form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitForm(form);
    });
  },

  bindFormValidation(form) {
    form.pristineValidation = PristineValidation(form);

    const categorySelect = form.querySelector('[data-service-categories-select]');
    const tagSelect = form.querySelector('[data-service-tags-select]');

    form.pristineValidation.addValidator(
      categorySelect,
      (value) => value.length > 0 && value.length <= 3,
      'Please select between 1 and 3 categories',
    );

    form.pristineValidation.addValidator(
      tagSelect,
      (value) => value.length >= 5 && value.length <= 20,
      'Please select between 5 and 20 tags',
    );
  },

  // pluralize 'days', 'weeks', 'months' based on the selected time amount
  bindTimeSelectPluralize(form) {
    form.addEventListener('change', () => {
      const timeTypeSelect = form.querySelector('[data-service-time-type-select]');
      const timeAmount = parseInt(form.querySelector('[data-service-time-amount-select]')?.value || 1, 10);
      if (!timeTypeSelect) return;

      timeTypeSelect.querySelectorAll('drb-option').forEach((option) => {
        const baseLabel = option.label.replace(/s$/, '');
        const shouldBePlural = timeAmount !== 1;
        const newLabel = baseLabel + (shouldBePlural ? 's' : '');

        option.label = newLabel;
        option.innerText = newLabel;
      });

      timeTypeSelect.selectionChanged();
    });
  },

  bindSelectMediaDialog(form, dialog) {
    form.addEventListener('click', (e) => {
      const addMediaBtn = e.target.closest('[data-open-service-media-select-dialog]');
      if (!addMediaBtn) return;

      ServiceSelectMediaDialog.open((userUploads) => {
        this.setSelectedUserUploads(dialog, userUploads);
      }, this.getSelectedUserUploads(dialog));
    });
  },

  bindMediaListeners(form) {
    form.addEventListener('click', (e) => {
      const mediaItemRemoveBtn = e.target.closest('[data-service-form-media-remove]');
      const mediaItem = mediaItemRemoveBtn?.closest('[data-service-form-media-item]');
      if (!mediaItemRemoveBtn || !mediaItem) return;

      mediaItem.remove();
      this.syncMediaContainerVisibility(form);
    });
  },

  getSelectedUserUploads(dialog, idsOnly = false) {
    const mediaItems = dialog.querySelectorAll('[data-service-form-media-item]');

    return Array.from(mediaItems).map((item) => (idsOnly ? item.getAttribute('data-service-form-media-item') : {
      id: item.getAttribute('data-service-form-media-item'),
      imageUrl: item.getAttribute('data-image-url'),
      hoverMediaSrc: item.querySelector('drb-thumbnail')?.getAttribute('hover-media-src') || '',
      hoverMediaType: item.querySelector('drb-thumbnail')?.getAttribute('hover-media-type') || '',
    }));
  },

  // show/hide the corresponding container based on the selected user uploads (empty or not)
  syncMediaContainerVisibility(dialog) {
    const emptyMediaContainer = dialog.querySelector('[data-service-form-media-empty-container]');
    const mediaContainer = dialog.querySelector('[data-service-form-media-container]');
    const userUploads = this.getSelectedUserUploads(dialog);

    emptyMediaContainer.toggleAttribute('hidden', userUploads.length > 0);
    mediaContainer.toggleAttribute('hidden', userUploads.length === 0);
  },

  setSelectedUserUploads(dialog, userUploads) {
    const mediaItemTemplate = document.querySelector('template#service-form-media-item');
    const mediaItemsContainer = dialog.querySelector('[data-service-form-media-items]');
    if (!mediaItemTemplate || !mediaItemsContainer) return;

    // clear existing media items
    mediaItemsContainer.innerHTML = '';

    // add new media items using template
    userUploads.forEach((userUpload) => {
      const mediaItem = mediaItemTemplate.content.cloneNode(true).firstElementChild;
      const mediaItemImage = mediaItem.querySelector('[data-service-form-media-image]');
      const mediaItemDrbThumbnail = mediaItem.querySelector('drb-thumbnail');

      if (mediaItemImage) mediaItemImage.src = generateResizedMediaUrl({ mediaUrl: userUpload.imageUrl, width: 800, height: 600 });
      mediaItem.setAttribute('data-service-form-media-item', userUpload.id);
      mediaItem.setAttribute('data-image-url', userUpload.imageUrl);
      mediaItemDrbThumbnail?.setAttribute('hover-media-src', userUpload.hoverMediaSrc || '');
      mediaItemDrbThumbnail?.setAttribute('hover-media-type', userUpload.hoverMediaType || '');

      mediaItemsContainer.appendChild(mediaItem);
    });

    this.syncMediaContainerVisibility(dialog);
  },

  async submitForm(form) {
    if (form.hasAttribute('is-submitting')) return;

    const data = this.getFormDataObject(form);
    const showPublishWarning = form.hasAttribute('data-show-publish-warning');

    const defaultServerErrorMsg = 'Failed to save service';
    const submitBtn = form.querySelector('[data-service-form-submit]');
    const formError = form.querySelector('[data-form-error]');
    const isEditing = form.hasAttribute('data-is-editing');

    if (!form.pristineValidation?.validate()) {
      shakeElement(submitBtn);

      const firstError = form.querySelector('.has-validation-error');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      return;
    }

    // disable the submit button and show loading state
    const toggleFormSubmitting = (isSubmitting) => {
      form.toggleAttribute('is-submitting', isSubmitting);
      submitBtn.disabled = isSubmitting;
      submitBtn.classList.toggle('btn2--loading', isSubmitting);
    };

    // incase of server error, show error message
    const setServerErrorMsg = (errorMsg) => {
      if (!formError) return;
      formError.toggleAttribute('hidden', !errorMsg);
      formError.innerText = errorMsg;
    };

    try {
      // ensure there is at least 1 selected media
      if (this.getSelectedUserUploads(form).length === 0) {
        throw new Error('Please select at least 1 shot image');
      }

      // show warning confirmation dialog if `published` & `instant checkout` is enabled (and it's not already published)
      if (showPublishWarning && data.published && data.instant_checkout && !form.hasConfirmedWarning) {
        DrbConfirm.open({
          title: 'Are you sure?',
          message: 'Enabling Quick Hire allows clients to book and pay instantly, committing you to deliver the selected service.',
          iconHtml: questionIcon,
          centered: true,
          cancelText: '',
          confirmClass: 'btn2--primary-alt',
          confirmText: 'Publish Service',
          onConfirm: () => {
            form.hasConfirmedWarning = true;
            this.submitForm(form);
          },
        });

        return;
      }

      // reset state
      toggleFormSubmitting(true);
      setServerErrorMsg(null);

      const response = await fetch(form.getAttribute('action'), {
        method: isEditing ? 'PUT' : 'POST',
        body: JSON.stringify({ service: data }),
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        // use error message provided by BE if available (422), otherwise use default
        const errorMsg = response.status === 422 ? responseText : defaultServerErrorMsg;
        throw new Error(errorMsg || defaultServerErrorMsg);
      }

      // reload page with success query param to show flash message
      const url = new URL(window.location.href);
      const urlParam = showPublishWarning && data.published ? 'service_published' : 'service_saved';
      url.searchParams.set(urlParam, 'true');

      window.location.href = url.toString();
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : defaultServerErrorMsg;
      setServerErrorMsg(errorMsg);
      console.error(errorMsg, error);

      toggleFormSubmitting(false);
    }
  },

  getFormDataObject(form) {
    const formData = new FormData(form);

    // get temp fields
    const timeAmount = formData.get('temp_time_amount');
    const timeType = formData.get('temp_time_type');
    const costDollars = formData.get('temp_cost_dollars');

    // time to complete (ISO 8601 duration)
    if (timeAmount && timeType) {
      formData.set('time_to_complete', `P${timeAmount}${timeType[0].toUpperCase()}`);
    }

    // convert budget dollars to cents
    if (costDollars) {
      formData.set('cost_cents', parseInt(costDollars, 10) * 100);
    }

    // remove temp fields
    formData.delete('temp_time_amount');
    formData.delete('temp_time_type');
    formData.delete('temp_cost_dollars');

    // build form object (preserve array fields)
    const arrayFields = ['categories', 'service_offering_tags'];
    const formObject = {};

    formData.forEach((value, key) => {
      if (arrayFields.includes(key)) {
        formObject[key] = Array.from(formData.getAll(key));
      } else {
        formObject[key] = value;
      }
    });

    // add selected media
    formObject.user_uploads = this.getSelectedUserUploads(form, true);

    // add milestone data
    const milestonesPayload = form.milestoneManager?.getMilestonesPayload() || [];
    formObject.engagement_milestones_attributes = milestonesPayload;

    return formObject;
  },

  bindProjectCostBreakdown(form) {
    const projectCostInput = form.querySelector('#service-form-price');

    projectCostInput?.addEventListener('input', () => {
      this.refetchCostBreakdown(form);
    });

    form.addEventListener('change', (e) => {
      const milestonesChanged = e.target.closest('[data-milestone-manager]');
      if (!milestonesChanged) return;

      this.refetchCostBreakdown(form);
    });
  },

  refetchCostBreakdown(form) {
    const milestonesPayload = form.milestoneManager?.getMilestonesPayload() || [];

    ProjectCostBreakdown.refetch({
      form,
      path: '/service_offerings/pricing/',
      previewMilestones: milestonesPayload.map((milestone) => ({
        step: milestone.step,
        description: milestone.description,
        cost_cents: milestone.amount_cents,
      })),
    });
  },

  bindDeleteButton(form) {
    const deleteBtn = form.querySelector('[data-service-form-delete]');
    if (!deleteBtn) return;

    deleteBtn.addEventListener('click', (e) => {
      const deletePath = deleteBtn.getAttribute('data-delete-path');
      e.preventDefault();
      this.deleteService(deletePath);
    });
  },

  deleteService(deletePath) {
    DrbConfirm.open({
      title: 'Are you sure you want to delete this Service?',
      message: '',
      iconHtml: questionIcon,
      centered: true,
      cancelText: 'Cancel',
      confirmClass: 'btn2--primary',
      confirmText: 'Delete Service',
      onConfirm: async () => {
        try {
          await fetch(deletePath, {
            method: 'DELETE',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Content-Type': 'application/json',
              'X-CSRF-Token': getCSRFToken(),
            },
          });

          // reload page with success query param to show flash message
          const url = new URL(window.location.href);
          const urlParam = 'service_deleted';
          url.searchParams.set(urlParam, 'true');

          window.location.href = url.toString();
        } catch (e) {
          DrbConfirm.open({
            title: 'Something went wrong!',
            message: 'Please try again.',
            iconHtml: questionIcon,
            centered: true,
            cancelText: 'Close',
            confirmClass: 'btn2--primary',
            confirmText: '',
          });
        }
      },
    });
  },
};
